import React from 'react';
import { Box, Typography } from '@mui/material';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  teal:        '#0E9F7E',
  tealLight:   '#ECFDF5',
  tealMid:     '#34D399',
  tealDark:    '#065F46',
  slate2:      '#1E293B',
  slate4:      '#94A3B8',
  border:      '#E2E8F0',
  white:       '#FFFFFF',
  amber:       '#D97706',
  amberLight:  '#FFFBEB',
  blue:        '#2563EB',
  blueLight:   '#EFF6FF',
  purple:      '#7C3AED',
  purpleLight: '#F5F3FF',
};

// ─── Action button ────────────────────────────────────────────────────────────
// Matches the full-width bordered buttons in the HTML sidebar
const ActionBtn = ({ iconBg, iconStroke, svgPath, label, onClick, disabled }) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: '9px',
      width: '100%',
      bgcolor: C.white, color: C.slate2,
      border: `1px solid ${C.border}`,
      borderRadius: '8px', p: '8px 11px',
      fontSize: 12, fontWeight: 600,
      fontFamily: 'inherit',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition: 'all 0.15s',
      pointerEvents: disabled ? 'none' : 'auto',
      ...(!disabled && {
        '&:hover': {
          borderColor: C.tealMid,
          bgcolor: C.tealLight,
          color: C.tealDark,
        },
      }),
    }}
  >
    {/* Coloured icon square */}
    <Box
      sx={{
        width: 26, height: 26, borderRadius: '7px',
        bgcolor: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg
        width="13" height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke={iconStroke}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {svgPath}
      </svg>
    </Box>
    {label}
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * QuickActions
 *
 * Props:
 *  onNewTask      : () => void
 *  onAddNote      : () => void
 *  onLogSiteVisit : () => void
 */
const QuickActions = ({ onNewTask, onAddNote, onLogSiteVisit, disabled = false }) => (
  // Matches `.sb-section` in the HTML
  <Box sx={{ p: '12px 16px', borderBottom: `1px solid ${C.border}` }}>

    {/* Section title */}
    <Typography
      sx={{
        fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: C.slate4, mb: '10px',
      }}
    >
      Quick Actions
    </Typography>

    {/* Buttons */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

      {/* New Task — amber icon */}
      <ActionBtn
        iconBg={C.amberLight}
        iconStroke="#92400E"
        disabled={disabled}
        svgPath={
          <>
            <polyline points="9 11 12 14 22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </>
        }
        label="New Task"
        onClick={onNewTask || (() => {})}
      />

      {/* Add Note — blue icon */}
      <ActionBtn
        iconBg={C.blueLight}
        iconStroke={C.blue}
        disabled={disabled}
        svgPath={
          <>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </>
        }
        label="Add Note"
        onClick={onAddNote || (() => {})}
      />

      {/* Schedule Site Visit — purple icon */}
      <ActionBtn
        iconBg={C.purpleLight}
        iconStroke={C.purple}
        disabled={disabled}
        svgPath={
          <>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </>
        }
        label="Schedule Site Visit"
        onClick={onLogSiteVisit || (() => {})}
      />

    </Box>
  </Box>
);

export default QuickActions;